import { neutralizeCsvFormula } from "@/lib/csv-safe";
/**
 * Reports → Follow Up subpage.
 * List of past submitted reports with filters, search, tabs (mine, drafts,
 * follow-ups, pending review, closed, all-org), CSV export, and per-row
 * deep-links to view, update, add notes, escalate, or close.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ArrowLeft, Search, AlertTriangle, Clock, MapPin, ArrowRight, FileText,
  ShieldAlert, Flame, Paperclip, Download, Filter as FilterIcon, Link2,
} from "lucide-react";
import {
  REPORT_TYPE_MAP, REPORT_TYPES, isPrivilegedRole, type ReportType,
} from "@/lib/report-types";
import { SEVERITY_STYLES, STATUS_STYLES, INCIDENT_SEVERITIES } from "@/lib/incident-types";

export const Route = createFileRoute("/_authenticated/reports/follow-up")({
  component: FollowUpPage,
  head: () => ({
    meta: [
      { title: "Follow Up — Reports — MissionReady" },
      { name: "description", content: "Past submitted reports — view, update, add notes, escalate, or close." },
    ],
  }),
});

type ReportRow = {
  id: string;
  reference_number: string;
  organization_id: string;
  reporter_id: string;
  assignee_id: string | null;
  site_id: string | null;
  category: string;
  subtype: string | null;
  severity: string;
  status: string;
  title: string | null;
  occurrence_at: string;
  created_at: string;
  report_type: ReportType | null;
  restricted: boolean;
  parent_incident_id: string | null;
  escalated: boolean | null;
};

type Site = { id: string; name: string };
type TabKey = "mine" | "drafts" | "followups" | "pending" | "closed" | "all";

function FollowUpPage() {
  const { currentOrg, user, currentRole } = useAuth();
  const privileged = isPrivilegedRole(currentRole);

  const [items, setItems] = useState<ReportRow[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [attachmentCounts, setAttachmentCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("mine");
  const [search, setSearch] = useState("");

  const [filterType, setFilterType] = useState<string>("all");
  const [filterSite, setFilterSite] = useState<string>("all");
  const [filterSeverity, setFilterSeverity] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const [{ data: rows }, { data: ss }] = await Promise.all([
      supabase
        .from("incidents")
        .select("id,reference_number,organization_id,reporter_id,assignee_id,site_id,category,subtype,severity,status,title,occurrence_at,created_at,report_type,restricted,parent_incident_id,escalated")
        .eq("organization_id", currentOrg.id)
        .order("created_at", { ascending: false })
        .limit(300),
      supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id),
    ]);
    const list = (rows as ReportRow[]) ?? [];
    setItems(list);
    setSites((ss as Site[]) ?? []);

    if (list.length > 0) {
      const ids = list.map((r) => r.id);
      const { data: atts } = await supabase
        .from("incident_attachments")
        .select("incident_id")
        .in("incident_id", ids);
      const counts: Record<string, number> = {};
      for (const a of (atts as { incident_id: string }[]) ?? []) {
        counts[a.incident_id] = (counts[a.incident_id] ?? 0) + 1;
      }
      setAttachmentCounts(counts);
    } else {
      setAttachmentCounts({});
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (!currentOrg) return;
    const ch = supabase
      .channel(`reports-followup-${currentOrg.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "incidents", filter: `organization_id=eq.${currentOrg.id}` },
        () => void load(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const siteName = (id: string | null) =>
    id ? (sites.find((s) => s.id === id)?.name ?? "—") : "Unassigned";

  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((r) => {
      if (filterType !== "all" && r.report_type !== filterType) return false;
      if (filterSite !== "all" && r.site_id !== filterSite) return false;
      if (filterSeverity !== "all" && r.severity !== filterSeverity) return false;
      if (q) {
        const hay = `${r.reference_number} ${r.title ?? ""} ${r.subtype ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, filterType, filterSite, filterSeverity]);

  const buckets = useMemo(() => {
    const me = user?.id;
    return {
      mine: filteredAll.filter((r) => r.reporter_id === me && r.status !== "draft"),
      drafts: filteredAll.filter((r) => r.reporter_id === me && r.status === "draft"),
      followups: filteredAll.filter(
        (r) => r.report_type === "supplemental" || r.parent_incident_id !== null,
      ),
      pending: filteredAll.filter(
        (r) => r.status === "pending_review" &&
          (privileged || r.reporter_id === me || r.assignee_id === me),
      ),
      closed: filteredAll.filter((r) => r.status === "resolved" || r.status === "closed"),
      all: privileged ? filteredAll : [],
    };
  }, [filteredAll, user?.id, privileged]);

  const counts = {
    mine: buckets.mine.length,
    drafts: buckets.drafts.length,
    followups: buckets.followups.length,
    pending: buckets.pending.length,
    closed: buckets.closed.length,
    all: buckets.all.length,
  };

  const exportCsv = () => {
    const rows = buckets[tab];
    if (rows.length === 0) return;
    const header = ["Reference","Type","Title","Severity","Status","Site","Occurrence","Created","Escalated","Restricted","Attachments"];
    const lines = [header.join(",")];
    for (const r of rows) {
      const cells = [
        r.reference_number,
        REPORT_TYPE_MAP[r.report_type ?? ("critical_incident" as ReportType)]?.short ?? "",
        r.title ?? r.subtype ?? "",
        r.severity,
        r.status,
        siteName(r.site_id),
        new Date(r.occurrence_at).toISOString(),
        new Date(r.created_at).toISOString(),
        r.escalated ? "yes" : "no",
        r.restricted ? "yes" : "no",
        String(attachmentCounts[r.id] ?? 0),
      ].map((c) => `"${neutralizeCsvFormula(c).replace(/"/g, '""')}"`);
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reports-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-4 pb-28 pt-6 md:px-8 md:py-8 md:pb-8">
      <div className="mx-auto max-w-5xl">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground hover:text-foreground">
          <Link to="/reports">
            <ArrowLeft className="mr-1 h-4 w-4" /> Reports
          </Link>
        </Button>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Follow Up</p>
            <h1 className="mt-1 font-display text-2xl font-bold md:text-3xl">Past Reports</h1>
            <p className="text-sm text-muted-foreground">
              View, update, add notes, escalate, or close past reports.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={buckets[tab].length === 0}>
              <Download className="mr-1 h-4 w-4" /> Export
            </Button>
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input aria-label="Search by reference, title, type"
              className="h-11 pl-9"
              placeholder="Search by reference, title, type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button
            variant={showFilters || filterType !== "all" || filterSite !== "all" || filterSeverity !== "all" ? "default" : "outline"}
            size="icon"
            className="h-11 w-11 shrink-0"
            onClick={() => setShowFilters((s) => !s)}
            aria-label="Filters"
          >
            <FilterIcon className="h-4 w-4" />
          </Button>
        </div>

        {showFilters && (
          <div className="mt-3 grid gap-2 rounded-xl border border-[#C0C0C0]/15 bg-card p-3 sm:grid-cols-3">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger aria-label="All types" className="h-10"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All report types</SelectItem>
                {REPORT_TYPES.map((m) => (
                  <SelectItem key={m.type} value={m.type}>{m.short}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSite} onValueChange={setFilterSite}>
              <SelectTrigger aria-label="All sites" className="h-10"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sites</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger aria-label="All severities" className="h-10"><SelectValue placeholder="All severities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                {INCIDENT_SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)} className="mt-5">
          <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-lg bg-muted/40 p-1">
            <TabTrigger value="mine" label="My Reports" count={counts.mine} />
            <TabTrigger value="drafts" label="Drafts" count={counts.drafts} />
            <TabTrigger value="followups" label="Follow-Ups" count={counts.followups} />
            <TabTrigger value="pending" label="Pending Review" count={counts.pending} />
            <TabTrigger value="closed" label="Closed" count={counts.closed} />
            {privileged && <TabTrigger value="all" label="All (Org)" count={counts.all} />}
          </TabsList>

          {(["mine","drafts","followups","pending","closed","all"] as TabKey[]).map((key) => (
            <TabsContent key={key} value={key} className="mt-4">
              <ReportList
                loading={loading}
                rows={buckets[key]}
                siteName={siteName}
                attachmentCounts={attachmentCounts}
                emptyLabel={EMPTY_LABEL[key]}
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

function TabTrigger({ value, label, count }: { value: string; label: string; count: number }) {
  return (
    <TabsTrigger
      value={value}
      className="shrink-0 rounded-md px-3 py-1.5 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
    >
      {label}
      <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground data-[state=active]:bg-primary/15 data-[state=active]:text-primary">
        {count}
      </span>
    </TabsTrigger>
  );
}

const EMPTY_LABEL: Record<TabKey, string> = {
  mine: "You haven't filed any reports yet.",
  drafts: "No drafts. Start a new report and tap Save Draft.",
  followups: "No follow-up or supplemental reports yet.",
  pending: "Nothing waiting for review right now.",
  closed: "No closed reports yet.",
  all: "No reports match the current filters.",
};

function ReportList({
  loading, rows, siteName, attachmentCounts, emptyLabel,
}: {
  loading: boolean;
  rows: ReportRow[];
  siteName: (id: string | null) => string;
  attachmentCounts: Record<string, number>;
  emptyLabel: string;
}) {
  if (loading) {
    return (
      <div className="rounded-xl border border-[#C0C0C0]/15 bg-card px-5 py-12 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-[#C0C0C0]/15 bg-card px-5 py-12 text-center text-sm text-muted-foreground">
        <FileText className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
        {emptyLabel}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[#C0C0C0]/10 rounded-xl border border-[#C0C0C0]/15 bg-card">
      {rows.map((r) => {
        const meta = r.report_type ? REPORT_TYPE_MAP[r.report_type] : null;
        const Icon = meta?.icon ?? AlertTriangle;
        const attCount = attachmentCounts[r.id] ?? 0;
        const isDraft = r.status === "draft";
        const linkProps = isDraft
          ? ({
              to: "/reports/new/$type",
              params: { type: r.report_type ?? "critical_incident" },
              search: { draftId: r.id },
            } as const)
          : ({
              to: "/incidents/$incidentId",
              params: { incidentId: r.id },
            } as const);
        return (
          <li key={r.id}>
            <Link
              {...linkProps}
              className="flex items-start gap-3 px-4 py-4 transition-colors hover:bg-muted/30 sm:px-5"
            >
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
                r.escalated ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"
              }`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {r.reference_number}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${SEVERITY_STYLES[r.severity] ?? ""}`}>
                    {r.severity}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_STYLES[r.status] ?? "bg-muted text-muted-foreground"}`}>
                    {r.status.replace("_", " ")}
                  </span>
                  {r.escalated && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-destructive">
                      <Flame className="h-3 w-3" /> Escalated
                    </span>
                  )}
                  {r.restricted && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-destructive">
                      <ShieldAlert className="h-3 w-3" /> Restricted
                    </span>
                  )}
                  {r.parent_incident_id && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-accent-foreground">
                      <Link2 className="h-3 w-3" /> Linked
                    </span>
                  )}
                </div>
                <div className="mt-1 truncate text-sm font-medium">
                  {r.title || meta?.short || r.subtype || "Report"}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />{siteName(r.site_id)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />{new Date(r.occurrence_at).toLocaleString()}
                  </span>
                  {attCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Paperclip className="h-3 w-3" />{attCount}
                    </span>
                  )}
                </div>
              </div>
              <ArrowRight className="mt-3 h-4 w-4 text-muted-foreground" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
